const DB_NAME = 'AsaBrancaFormsDB';
const STORE_DRAFTS = 'form_drafts';
const STORE_SYNC_QUEUE = 'sync_queue';
const DB_VERSION = 2; // Bumped version for migration

const checkStorageQuota = async () => {
    if (navigator.storage && navigator.storage.estimate) {
        try {
            const { usage, quota } = await navigator.storage.estimate();
            if (usage !== undefined && quota !== undefined) {
                const percent = (usage / quota) * 100;
                if (percent > 80) {
                    console.warn(`[FormStorage] High storage usage: ${percent.toFixed(1)}%`);
                }
                if (percent > 95) {
                    throw new Error("Armazenamento local cheio! Libere espaço no dispositivo para continuar salvando dados.");
                }
            }
        } catch (e) {
            console.error("[FormStorage] Error checking quota:", e);
            if ((e as Error).message.includes("Armazenamento local cheio")) throw e;
        }
    }
};

/**
 * Open (or create) the IndexedDB database.
 */
const openDB = (): Promise<IDBDatabase> => {
  return new Promise((resolve, reject) => {
    if (!('indexedDB' in window)) {
      reject(new Error('IndexedDB not supported'));
      return;
    }

    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = (event) => {
      console.error('[FormStorage] Database error:', (event.target as any).error);
      reject((event.target as any).error);
    };

    request.onsuccess = (event) => {
      resolve((event.target as IDBOpenDBRequest).result);
    };

    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;

      // Store 1: Drafts
      if (!db.objectStoreNames.contains(STORE_DRAFTS)) {
        db.createObjectStore(STORE_DRAFTS, { keyPath: 'formId' });
      }

      // Store 2: Sync Queue (Outbox)
      if (!db.objectStoreNames.contains(STORE_SYNC_QUEUE)) {
        db.createObjectStore(STORE_SYNC_QUEUE, { keyPath: 'id', autoIncrement: true });
      }
    };
  });
};

/**
 * Save a form draft to IndexedDB.
 */
export const saveDraft = async (formId: string, data: any): Promise<void> => {
  try {
    await checkStorageQuota();
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DRAFTS], 'readwrite');
      const store = transaction.objectStore(STORE_DRAFTS);

      const record = {
        formId,
        data,
        updatedAt: new Date().toISOString()
      };

      const request = store.put(record);

      request.onsuccess = () => resolve();
      request.onerror = (e) => reject((e.target as any).error);
    });
  } catch (error) {
    console.error('[FormStorage] Error saving draft:', error);
  }
};

/**
 * Retrieve a form draft from IndexedDB.
 */
export const getDraft = async (formId: string): Promise<any | null> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DRAFTS], 'readonly');
      const store = transaction.objectStore(STORE_DRAFTS);
      const request = store.get(formId);

      request.onsuccess = () => {
        const result = request.result;
        resolve(result ? result.data : null);
      };
      request.onerror = (e) => reject((e.target as any).error);
    });
  } catch (error) {
    console.error('[FormStorage] Error getting draft:', error);
    return null;
  }
};

/**
 * Delete a form draft from IndexedDB.
 */
export const deleteDraft = async (formId: string): Promise<void> => {
  try {
    const db = await openDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction([STORE_DRAFTS], 'readwrite');
      const store = transaction.objectStore(STORE_DRAFTS);
      const request = store.delete(formId);

      request.onsuccess = () => resolve();
      request.onerror = (e) => reject((e.target as any).error);
    });
  } catch (error) {
    console.error('[FormStorage] Error deleting draft:', error);
  }
};

// --- SYNC QUEUE OPERATIONS ---

export const enqueueSubmission = async (submissionPayload: any): Promise<void> => {
    try {
        await checkStorageQuota();
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_SYNC_QUEUE], 'readwrite');
            const store = transaction.objectStore(STORE_SYNC_QUEUE);

            // We store the payload + metadata
            const record = {
                payload: submissionPayload,
                timestamp: new Date().toISOString(),
                retryCount: 0
            };

            const request = store.add(record);
            request.onsuccess = () => {
                console.log('[SyncQueue] Submission enqueued for offline sync.');
                window.dispatchEvent(new Event('sync-queue-change'));
                resolve();
            };
            request.onerror = (e) => reject((e.target as any).error);
        });
    } catch (error) {
        console.error('[SyncQueue] Error enqueuing:', error);
        throw error; // Re-throw so caller knows
    }
};

export const getPendingSubmissions = async (): Promise<any[]> => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_SYNC_QUEUE], 'readonly');
            const store = transaction.objectStore(STORE_SYNC_QUEUE);
            const request = store.getAll();

            request.onsuccess = () => resolve(request.result || []);
            request.onerror = (e) => reject((e.target as any).error);
        });
    } catch (error) {
        console.error('[SyncQueue] Error getting pending:', error);
        return [];
    }
};

export const deleteSubmissionFromQueue = async (id: number): Promise<void> => {
    try {
        const db = await openDB();
        return new Promise((resolve, reject) => {
            const transaction = db.transaction([STORE_SYNC_QUEUE], 'readwrite');
            const store = transaction.objectStore(STORE_SYNC_QUEUE);
            const request = store.delete(id);

            request.onsuccess = () => {
                window.dispatchEvent(new Event('sync-queue-change'));
                resolve();
            };
            request.onerror = (e) => reject((e.target as any).error);
        });
    } catch (error) {
        console.error('[SyncQueue] Error deleting from queue:', error);
    }
};
